import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useState } from 'react'
import { Loader2, CheckCircle, AlertCircle, XCircle } from 'lucide-react'
import {
  acceptInvitationSchema,
  type AcceptInvitationInput,
} from '@dcp/shared'
import {
  useInvitationByToken,
  useAcceptInvitation,
  useAcceptInvitationAsExistingUser,
  type OrganizationInvitation,
} from '@/hooks/useOrganizationInvitations'
import { useAuth } from '@/contexts/AuthContext'
import { computePostAcceptRedirect } from '@/lib/post-accept-redirect'
import { PasswordField } from '@/components/ui/password-field'
import { MFAVerifyStep } from '@/components/auth/MFAVerifyStep'
import {
  AsyncButton,
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
} from '@atrvd/saas-ui'

/**
 * CenteredLoader component
 * Displays a centered loading spinner
 */
function CenteredLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  )
}

/**
 * ErrorCard component
 * Displays an error message when invitation is invalid or not found
 * @param message - The error message to display
 */
function ErrorCard({ message }: { message: string }) {
  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-center mb-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">
          Invalid Invitation
        </CardTitle>
        <CardDescription className="text-center">{message}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild className="w-full">
          <Link to="/login">Go to Login</Link>
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * ExpiredCard component
 * Displays a message when the invitation has expired
 */
function ExpiredCard() {
  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-center mb-4">
          <AlertCircle className="h-12 w-12 text-amber-500" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">
          Invitation Expired
        </CardTitle>
        <CardDescription className="text-center">
          This invitation has expired. Please contact your organization
          administrator to request a new invitation.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild className="w-full">
          <Link to="/login">Go to Login</Link>
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * AlreadyAcceptedCard component
 * Displays a message when the invitation has already been accepted
 */
function AlreadyAcceptedCard() {
  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-center mb-4">
          <CheckCircle className="h-12 w-12 text-green-500" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">
          Already Accepted
        </CardTitle>
        <CardDescription className="text-center">
          This invitation has already been accepted. You can now log in with
          your credentials.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild className="w-full">
          <Link to="/login">Go to Login</Link>
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * ProcessingCard component
 * Displays a message when the invitation is currently being processed —
 * either by the same user in another tab/double-submit or, rarely, stuck
 * after a backend crash. Tells the user to wait/refresh and to contact the
 * admin if the state persists.
 */
function ProcessingCard() {
  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-center mb-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">
          Processing Invitation
        </CardTitle>
        <CardDescription className="text-center">
          This invitation is currently being processed. If you opened it in
          another tab, please continue there. If this page doesn&apos;t update
          shortly, refresh — and contact your administrator if the issue
          persists.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild className="w-full">
          <Link to="/login">Go to Login</Link>
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * CancelledCard component
 * Displays a message when the invitation has been cancelled/revoked
 */
function CancelledCard() {
  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-center mb-4">
          <XCircle className="h-12 w-12 text-destructive" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">
          Invitation Cancelled
        </CardTitle>
        <CardDescription className="text-center">
          This invitation has been cancelled by your organization administrator.
          Please contact them if you need a new invitation.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild className="w-full">
          <Link to="/login">Go to Login</Link>
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * SuccessCard component
 * Displays a success message after accepting the invitation and performs auto-login.
 * On successful sign-in, navigates to the role-appropriate destination route
 * rather than a hardcoded path, so users who lack view_banking (e.g. treasury)
 * are never sent to a route they cannot access.
 *
 * @param organizationName - Name of the organization the user joined
 * @param email - User's email for auto-login
 * @param password - User's password for auto-login
 * @param redirectTo - Pre-computed destination route based on invite_type and product_role
 */
function SuccessCard({
  organizationName,
  email,
  password,
  redirectTo,
}: {
  organizationName: string
  email: string
  password: string
  /** Role-appropriate destination computed by computePostAcceptRedirect. */
  redirectTo: string
}) {
  const navigate = useNavigate()
  const { signIn } = useAuth()
  const [loginError, setLoginError] = useState<string | null>(null)
  const [isLoggingIn, setIsLoggingIn] = useState(true)

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const performAutoLogin = async () => {
      try {
        // Use AuthContext.signIn to ensure the suspension check runs
        const { error } = await signIn(email, password)

        if (error) {
          console.error('Auto-login failed:', error)
          setLoginError(error.message)
          setIsLoggingIn(false)
          // Fallback to login page after 3 seconds
          timeoutId = setTimeout(() => navigate('/login'), 3000)
          return
        }

        // Success — redirect to the role-appropriate route.
        navigate(redirectTo)
      } catch (err) {
        console.error('Auto-login error:', err)
        setLoginError('Failed to log in automatically')
        setIsLoggingIn(false)
        timeoutId = setTimeout(() => navigate('/login'), 3000)
      }
    }

    performAutoLogin()

    // Cleanup timeout on unmount to prevent memory leak
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [email, password, navigate, redirectTo, signIn])

  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-center mb-4">
          <CheckCircle className="h-12 w-12 text-green-500" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">
          Welcome to {organizationName}!
        </CardTitle>
        <CardDescription className="text-center">
          {loginError
            ? `${loginError}. Redirecting to login...`
            : isLoggingIn
              ? 'Your account has been created. Logging you in...'
              : 'Redirecting to dashboard...'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </CardContent>
    </Card>
  )
}

/**
 * Form shown when the invited address ALREADY has an account (DCP-350).
 *
 * The ordinary form asks a newcomer to choose a password, which for a taken
 * address is impossible — Supabase answers 422 email_exists. This one asks the
 * existing owner to sign in with the password they already have; joining a
 * second organization must not disturb the account they use in the first.
 *
 * Signing in here is also the security step: the server grants membership only
 * when the caller IS the invited address, so a leaked link is useless to anyone
 * else.
 *
 * @param invitation - The invitation details
 * @param onSubmit - Called with the entered password
 * @param error - Error message to display if sign-in or acceptance fails
 * @param isSubmitting - Whether sign-in / acceptance is in flight
 */
function ExistingAccountForm({
  invitation,
  onSubmit,
  error,
  isSubmitting,
}: {
  invitation: OrganizationInvitation
  onSubmit: (password: string) => void
  error: string | null
  isSubmitting: boolean
}) {
  const [password, setPassword] = useState('')

  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">
          Join {invitation.organization_name}
        </CardTitle>
        <CardDescription className="text-center">
          You already have a Daily Cash Projections account. Sign in to accept this
          invitation — your existing password stays the same.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (password) onSubmit(password)
          }}
          className="space-y-4"
        >
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="existing-email">Email</Label>
            <Input id="existing-email" value={invitation.admin_email} disabled readOnly />
          </div>

          <PasswordField
            id="existing-password"
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
          />

          <Button type="submit" className="w-full" disabled={isSubmitting || !password}>
            {isSubmitting ? 'Joining…' : 'Sign in and join'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * AcceptanceForm component
 * Form for accepting an invitation by setting a password
 * @param invitation - The invitation details
 * @param onSubmit - Callback when form is submitted
 * @param error - Error message to display if submission fails
 * @param isSubmitting - Whether the form is currently submitting
 */
function AcceptanceForm({
  invitation,
  onSubmit,
  error,
  isSubmitting,
}: {
  invitation: OrganizationInvitation
  onSubmit: (data: AcceptInvitationInput) => void
  error: string | null
  isSubmitting: boolean
}) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AcceptInvitationInput>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  // Watch password so the strength meter / toggle visibility reflect the
  // true form value, including across programmatic state changes.
  const passwordValue = watch('password') ?? ''

  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">
          Accept Invitation
        </CardTitle>
        <CardDescription className="text-center">
          You've been invited to join {invitation.organization_name}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="organization_name">Organization</Label>
            <Input
              id="organization_name"
              type="text"
              value={invitation.organization_name}
              disabled
              className="bg-muted"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin_email">Email</Label>
            <Input
              id="admin_email"
              type="email"
              value={invitation.admin_email}
              disabled
              className="bg-muted"
            />
          </div>

          <PasswordField
            id="password"
            label="Password"
            autoComplete="new-password"
            error={errors.password?.message}
            showStrengthMeter
            value={passwordValue}
            {...register('password')}
          />

          <PasswordField
            id="confirmPassword"
            label="Confirm Password"
            placeholder="Confirm your password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <AsyncButton
            type="submit"
            className="w-full"
            isLoading={isSubmitting}
            loadingText="Creating Account..."
          >
            Accept Invitation
          </AsyncButton>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * AcceptInvitationPage component
 * Public page where invited users can set their password and activate their account
 * Accessible via /invite/accept/:token
 *
 * Flow:
 * 1. Load invitation by token from URL
 * 2. Validate invitation status (open, expired, accepted, or cancelled)
 * 3. Show appropriate message for invalid statuses immediately (before form)
 * 4. If status is 'open', display acceptance form
 * 5. On submit, call accept mutation to create account
 * 6. On success, auto-login and redirect to dashboard
 */
export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>()
  const {
    data: invitation,
    isLoading,
    error,
  } = useInvitationByToken(token || '')
  const acceptMutation = useAcceptInvitation()
  const acceptAsExistingUser = useAcceptInvitationAsExistingUser()
  const navigate = useNavigate()
  const { signIn, signOut, completeMFALogin } = useAuth()
  // Store password for auto-login after successful account creation
  const [submittedPassword, setSubmittedPassword] = useState<string | null>(null)
  // DCP-350: sign-in + join runs as one user action, so its progress and error
  // are tracked here rather than on a single mutation.
  const [joinError, setJoinError] = useState<string | null>(null)
  const [isJoining, setIsJoining] = useState(false)
  // Shown when the invited account has TOTP enabled: the password alone leaves
  // the session at aal1, and the invitation must not be redeemed until the
  // owner has actually completed their own second factor (Presto PR #202
  // finding 3).
  const [showMFA, setShowMFA] = useState(false)

  /**
   * Accept as an EXISTING account: sign in first, then claim the invitation
   * with that session. Both steps are required — the server refuses the claim
   * unless the caller is the invited address.
   */
  const claimInvitation = useCallback(async () => {
    if (!token || !invitation) return

    try {
      await acceptAsExistingUser.mutateAsync({ token })
      navigate(computePostAcceptRedirect(invitation), { replace: true })
    } catch (error) {
      setJoinError(
        error instanceof Error ? error.message : 'Failed to join the organization',
      )
      setIsJoining(false)
      setShowMFA(false)
    }
  }, [token, invitation, acceptAsExistingUser, navigate])

  const handleJoinAsExistingUser = async (password: string) => {
    if (!token || !invitation) return

    setIsJoining(true)
    setJoinError(null)

    // signIn comes from AuthContext so the suspension check runs, exactly as on
    // the normal login screen. It also tells us whether TOTP is pending: on
    // `requiresMFA` the session is only aal1 and `state.user` is still null, so
    // claiming here would redeem the invitation without the account owner ever
    // completing their second factor — and then land them on a protected route
    // with a spent token.
    const { error: signInError, requiresMFA } = await signIn(invitation.admin_email, password)
    if (signInError) {
      setJoinError(signInError.message || 'Could not sign you in. Check your password and try again.')
      setIsJoining(false)
      return
    }

    if (requiresMFA) {
      setShowMFA(true)
      return
    }

    await claimInvitation()
  }

  /** MFA verified — finish the login, then claim the invitation. */
  const handleMFAVerified = useCallback(async () => {
    const { error } = await completeMFALogin()
    if (error) {
      setShowMFA(false)
      setIsJoining(false)
      setJoinError(error.message)
      return
    }
    await claimInvitation()
  }, [completeMFALogin, claimInvitation])

  /** Abandon the MFA step: drop the aal1 session so nothing is left half-signed-in. */
  const handleMFABack = useCallback(async () => {
    setShowMFA(false)
    setIsJoining(false)
    await signOut()
  }, [signOut])

  /**
   * Handle form submission
   * Calls the accept invitation mutation with token and password
   * Stores password for auto-login on success
   */
  const handleAccept = (data: AcceptInvitationInput) => {
    if (!token) return

    // Store password for auto-login
    setSubmittedPassword(data.password)

    acceptMutation.mutate(
      { token, password: data.password },
      {
        onError: (error) => {
          // Error will be displayed in the form via acceptMutation.error
          console.error('Failed to accept invitation:', error)
          // Clear password on error
          setSubmittedPassword(null)
        },
      }
    )
  }

  // Show loading spinner while fetching invitation
  if (isLoading) {
    return <CenteredLoader />
  }

  // Show error if invitation not found or failed to load
  if (error || !invitation) {
    return (
      <ErrorCard
        message={
          error instanceof Error
            ? error.message
            : 'Invalid or expired invitation'
        }
      />
    )
  }

  // Show expired message if invitation has expired
  if (invitation.status === 'expired') {
    return <ExpiredCard />
  }

  // Show already accepted message if invitation was already used
  if (invitation.status === 'accepted') {
    return <AlreadyAcceptedCard />
  }

  // Show cancelled message if invitation was revoked by admin
  if (invitation.status === 'cancelled') {
    return <CancelledCard />
  }

  // Mid-acceptance claim: another tab / double-submit / stuck row.
  // Surface explicitly instead of letting the user re-submit into a guaranteed
  // server-side rejection from the atomic claim guard.
  if (invitation.status === 'processing') {
    return <ProcessingCard />
  }

  // Show success message and perform auto-login after accepting invitation.
  // Compute the destination before rendering so SuccessCard can navigate
  // to the correct route for the user's invite_type and product_role.
  if (acceptMutation.isSuccess && submittedPassword) {
    return (
      <SuccessCard
        organizationName={invitation.organization_name}
        email={invitation.admin_email}
        password={submittedPassword}
        redirectTo={computePostAcceptRedirect(invitation)}
      />
    )
  }

  // TOTP challenge for an existing account mid-join. Rendered before the form
  // below so the invitation stays unclaimed until the code is verified.
  if (showMFA) {
    return <MFAVerifyStep onVerified={handleMFAVerified} onBack={handleMFABack} />
  }

  // DCP-350: an address that already has an account cannot go through the
  // create-account form — Supabase would answer 422 email_exists. Ask its owner
  // to sign in and confirm instead.
  if (invitation.user_exists) {
    return (
      <ExistingAccountForm
        invitation={invitation}
        onSubmit={handleJoinAsExistingUser}
        error={joinError}
        isSubmitting={isJoining}
      />
    )
  }

  // Show acceptance form for valid invitations
  return (
    <AcceptanceForm
      invitation={invitation}
      onSubmit={handleAccept}
      error={
        acceptMutation.error instanceof Error
          ? acceptMutation.error.message
          : null
      }
      isSubmitting={acceptMutation.isPending}
    />
  )
}